import { Box, Container } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Link } from 'react-router-dom';
import { getFeaturedReferenceGuides, KC_GUIDE_BASE } from '../repository';
import { KC_COLORS } from '../theme';

const GUIDE_VISUALS = {
  'artificial-intelligence': {
    Icon: PsychologyOutlinedIcon,
    glow: '#5EEAD4',
  },
  'machine-learning': {
    Icon: InsightsOutlinedIcon,
    glow: '#6EE7B7',
  },
  'generative-ai': {
    Icon: AutoAwesomeOutlinedIcon,
    glow: '#C4B5FD',
  },
  'large-language-models': {
    Icon: ChatBubbleOutlineRoundedIcon,
    glow: '#93C5FD',
  },
};

function GuideLink({ href, className, style, children }) {
  const isInternal = href?.startsWith('/knowledge-center/');
  if (isInternal) {
    return (
      <Link to={href} className={className} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href || '#kc-guides'} className={className} style={style}>
      {children}
    </a>
  );
}

export function FeaturedReferenceGuides() {
  const section = getFeaturedReferenceGuides();
  if (!section) return null;

  const { guides = [] } = section;
  const featuredGuides = guides.slice(0, 3);

  return (
    <Box
      id="kc-guides"
      className="kc-guides w-full py-8 md:py-10 scroll-mt-28"
      style={{ backgroundColor: KC_COLORS.lightGrey, textAlign: 'left' }}
    >
      <Container maxWidth="lg">
        <div
          className="rounded-[24px] border bg-white px-5 py-6 md:px-8 md:py-8 lg:px-10 lg:py-9 shadow-[0_10px_36px_rgba(10,29,58,0.08)]"
          style={{ borderColor: KC_COLORS.border, textAlign: 'left' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
            <div className="lg:col-span-5" style={{ textAlign: 'left' }}>
              {section.badge ? (
                <span
                  className="inline-flex items-center rounded-md px-2.5 py-1 kc-type-caption font-bold uppercase tracking-wide"
                  style={{
                    backgroundColor: KC_COLORS.teal,
                    color: KC_COLORS.white,
                  }}
                >
                  {section.badge}
                </span>
              ) : null}

              <h2
                className="kc-section-title m-0 mt-3"
                style={{
                  color: KC_COLORS.primaryNavy,
                  fontSize: '28px',
                  lineHeight: '36px',
                  fontWeight: 700,
                  textAlign: 'left',
                }}
              >
                {section.title}
              </h2>

              {section.description ? (
                <p
                  className="m-0 mt-3 mb-5"
                  style={{
                    color: KC_COLORS.gray600,
                    fontSize: '15px',
                    lineHeight: '24px',
                    textAlign: 'left',
                  }}
                >
                  {section.description}
                </p>
              ) : null}

              {section.highlights?.length ? (
                <ul className="m-0 mb-6 p-0 list-none flex flex-col gap-3">
                  {section.highlights.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3"
                      style={{
                        color: KC_COLORS.primaryNavy,
                        fontSize: '15px',
                        lineHeight: '22px',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5"
                        style={{ backgroundColor: KC_COLORS.teal }}
                      >
                        <CheckRoundedIcon
                          sx={{ fontSize: 14, color: KC_COLORS.white }}
                        />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="flex flex-wrap gap-3">
                {section.primaryCta ? (
                  <GuideLink
                    href={section.primaryCta.href}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 font-semibold no-underline transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: KC_COLORS.teal,
                      color: KC_COLORS.white,
                      fontSize: '15px',
                      lineHeight: '22px',
                    }}
                  >
                    {section.primaryCta.label}
                    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                  </GuideLink>
                ) : null}
                {section.secondaryCta ? (
                  <GuideLink
                    href={section.secondaryCta.href}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 font-semibold no-underline transition-colors"
                    style={{
                      border: `1.5px solid ${KC_COLORS.teal}`,
                      color: KC_COLORS.primaryNavy,
                      backgroundColor: KC_COLORS.white,
                      fontSize: '15px',
                      lineHeight: '22px',
                    }}
                  >
                    {section.secondaryCta.label}
                    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                  </GuideLink>
                ) : null}
              </div>
            </div>

            <div
              className="lg:col-span-7 flex flex-col h-full min-h-0"
              style={{ textAlign: 'left' }}
            >
              <div className="flex items-center justify-between gap-3 mb-4 shrink-0">
                <h3
                  className="m-0 uppercase tracking-[0.08em]"
                  style={{
                    color: KC_COLORS.primaryNavy,
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 700,
                    textAlign: 'left',
                  }}
                >
                  Featured Reference Guides
                </h3>
                <Link
                  to={KC_GUIDE_BASE}
                  className="font-semibold no-underline whitespace-nowrap"
                  style={{
                    color: KC_COLORS.teal,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  View all guides →
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-3.5 flex-1 min-h-0 items-stretch">
                {featuredGuides.map((guide) => {
                  const visual =
                    GUIDE_VISUALS[guide.id] || GUIDE_VISUALS['artificial-intelligence'];
                  const Icon = visual.Icon;

                  return (
                    <div
                      key={guide.id}
                      className="kc-guide-card flex flex-col h-full min-h-0"
                    >
                      <GuideLink
                        href={guide.href}
                        className="relative flex flex-1 flex-col overflow-hidden rounded-2xl p-4 md:p-4 min-h-[250px] no-underline"
                        style={{
                          backgroundColor: '#071526',
                          textAlign: 'left',
                        }}
                      >
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-28 opacity-70"
                          style={{
                            background: `radial-gradient(ellipse at 50% 100%, ${visual.glow}33 0%, transparent 70%), linear-gradient(180deg, transparent 0%, rgba(0,155,170,0.12) 100%)`,
                          }}
                          aria-hidden
                        />

                        <h4
                          className="relative m-0"
                          style={{
                            color: KC_COLORS.white,
                            fontSize: '16px',
                            lineHeight: '22px',
                            fontWeight: 700,
                            textAlign: 'left',
                          }}
                        >
                          {guide.title}
                        </h4>
                        <p
                          className="relative m-0 mt-1"
                          style={{
                            color: 'rgba(255,255,255,0.72)',
                            fontSize: '12px',
                            lineHeight: '16px',
                            textAlign: 'left',
                          }}
                        >
                          {guide.subtitle}
                        </p>

                        <div className="relative flex flex-1 items-center justify-center py-5">
                          <Icon
                            sx={{
                              fontSize: 64,
                              color: visual.glow,
                              filter: `drop-shadow(0 0 14px ${visual.glow}88)`,
                            }}
                          />
                        </div>

                        <div className="relative mt-auto">
                          {guide.pages != null ? (
                            <div
                              style={{
                                color: KC_COLORS.white,
                                fontSize: '11px',
                                lineHeight: '14px',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                textAlign: 'left',
                              }}
                            >
                              {guide.pages} pages
                            </div>
                          ) : null}
                          <div
                            className="mt-1"
                            style={{
                              color: 'rgba(255,255,255,0.65)',
                              fontSize: '11px',
                              lineHeight: '14px',
                              textAlign: 'left',
                            }}
                          >
                            {guide.updated}
                          </div>
                        </div>
                      </GuideLink>

                      <GuideLink
                        href={guide.href}
                        className="mt-2.5 inline-flex items-center gap-1 font-semibold no-underline transition-all hover:gap-2 shrink-0"
                        style={{
                          color: KC_COLORS.teal,
                          fontSize: '13px',
                          lineHeight: '18px',
                          textAlign: 'left',
                        }}
                      >
                        Read Guide
                        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                      </GuideLink>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Box>
  );
}
